import type { CopilotProposal } from "@repo/app-types/entities/copilotProposal";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { Size } from "@repo/ui/helpers/helpers";
import { useConfirmationModal } from "@repo/ui/hooks/useConfirmationModal";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { showToast, ToastVariant } from "@repo/ui/toast/toast";
import { IconChevronLeft } from "@tabler/icons-react";
import { useCallback, useEffect, useState, type FC } from "react";

import { useResolveProposalMutation } from "../../../api/entities/copilotProposal.js";
import { useDeleteKnowledgeBaseAttachmentMutation } from "../../../api/entities/knowledgeBase.js";
import { isConflictQueryError } from "../../../api/queryErrors.js";
import { CopilotProposalBanner } from "../../../components/copilotProposalBanner.js";
import { ResourceSaveFooter } from "../../../components/resourceSaveFooter.js";
import { useUnsavedChangesGuard } from "../../../hooks/useUnsavedChangesGuard.js";
import { AttachmentEditorContent } from "../../agents/components/attachmentEditorContent.js";
import { useAttachmentEditorState } from "../../agents/components/useAttachmentEditorState.js";
import { useDeepLinkedProposal } from "../../copilot/copilotDeepLinks.js";
import { useReportEditorProposal } from "../../copilot/editorProposalsSlice.js";
import { KnowledgeBaseSyncPill } from "./knowledgeBaseSyncPill.js";

/** An Ask Alpharun proposal to rewrite the document, opened from the chat. */
type LinkedProposal = CopilotProposal<"knowledgeBaseAttachment">;

interface KnowledgeBaseAttachmentEditorProps {
  knowledgeBaseId: string;
  attachmentId: string;
  /**
   * Whether the parent knowledge base is synced, once it has loaded. It
   * answers for the document until the document loads.
   */
  isKnowledgeBaseSynced?: boolean;
  onClose: () => void;
}

export const KnowledgeBaseAttachmentEditor: FC<
  KnowledgeBaseAttachmentEditorProps
> = (props) => {
  const { knowledgeBaseId, attachmentId, isKnowledgeBaseSynced, onClose } =
    props;

  const {
    attachment,
    isLoading,
    isSaving,
    isInitialized,
    editedBody,
    setEditedBody,
    fetchedBody,
    hasUnsavedChanges,
    onSave,
    onDiscard,
  } = useAttachmentEditorState({ knowledgeBaseId, attachmentId });
  const { guardModal, navigateWithoutConfirmation } =
    useUnsavedChangesGuard(hasUnsavedChanges);

  const [linkedProposal, setLinkedProposal] = useState<LinkedProposal | null>(
    null,
  );
  const [resolveProposal, { isLoading: isResolvingProposal }] =
    useResolveProposalMutation();

  // One-shot link from an Ask Alpharun card (?proposalId=...): the pending
  // proposal shows over the document until it is applied or discarded. A card
  // for another document loads nothing.
  const { row: deepLinkProposal, clear: clearDeepLinkProposalId } =
    useDeepLinkedProposal("knowledgeBaseAttachment", {
      onUnavailable: notifyProposalUnavailable,
    });
  useEffect(() => {
    if (!deepLinkProposal) {
      return;
    }
    clearDeepLinkProposalId();
    if (deepLinkProposal.resource_id === attachmentId) {
      setLinkedProposal(deepLinkProposal);
    }
  }, [deepLinkProposal, attachmentId, clearDeepLinkProposalId]);

  // A synced document can't be edited here. Until the document or its
  // knowledge base loads, it's read-only too, so Delete and the save footer
  // don't briefly appear on a synced document.
  const isReadOnly = attachment
    ? Boolean(attachment.sync)
    : (isKnowledgeBaseSynced ?? true);
  // A read-only document can't be saved, so a suggestion for it isn't shown.
  const shownProposal = isInitialized && !isReadOnly ? linkedProposal : null;

  // The chat marks the card this editor holds, and reads Saving while it saves.
  useReportEditorProposal(shownProposal?.id, isSaving);

  const [deleteAttachment, { isLoading: isDeleting }] =
    useDeleteKnowledgeBaseAttachmentMutation();

  /** Returns whether the body saved. */
  const saveBody = async (body: string): Promise<boolean> => {
    try {
      await onSave(body);
    } catch {
      showToast({
        variant: ToastVariant.Error,
        title: "Failed to save article",
        description: "Please try again.",
      });
      return false;
    }
    showToast({
      variant: ToastVariant.Success,
      title: "Article saved",
      description: "Your changes have been saved.",
    });
    return true;
  };

  // A proposal that has moved on (409) was already decided, so it closes too.
  // Any other failure keeps it shown, so the next click retries it.
  const resolveLinkedProposal = async (
    proposal: LinkedProposal,
    status: "applied" | "discarded",
  ) => {
    try {
      await resolveProposal({ proposalId: proposal.id, status }).unwrap();
    } catch (err) {
      if (!isConflictQueryError(err)) {
        showToast(
          status === "applied"
            ? {
                variant: ToastVariant.Error,
                title:
                  "The article was saved, but the suggestion could not be marked as applied.",
              }
            : {
                variant: ToastVariant.Error,
                title: "Failed to discard the suggestion",
                description: "Please try again.",
              },
        );
        return;
      }
      notifyProposalUnavailable();
    }
    setLinkedProposal(null);
  };

  // Apply saves the suggestion as the whole document. Its diff runs from the
  // current body, so anything changed since the rewrite was drafted shows up
  // there as removed.
  const applyLinkedProposal = async (proposal: LinkedProposal) => {
    const isSaved = await saveBody(proposal.data.body);
    if (!isSaved) {
      return;
    }
    setEditedBody(proposal.data.body);
    await resolveLinkedProposal(proposal, "applied");
  };

  const handleDelete = useCallback(async () => {
    try {
      await deleteAttachment({ knowledgeBaseId, attachmentId }).unwrap();
      showToast({
        title: `Successfully deleted "${attachment?.title || attachment?.file_name || ""}" document`,
        variant: ToastVariant.Success,
      });
      navigateWithoutConfirmation(onClose);
    } catch (err: any) {
      showToast({
        title: `Failed to delete document: ${err.data?.error || err.message || "Unknown error"}`,
        variant: ToastVariant.Error,
      });
    }
  }, [
    deleteAttachment,
    knowledgeBaseId,
    attachmentId,
    attachment,
    navigateWithoutConfirmation,
    onClose,
  ]);

  const {
    renderConfirmationModal: renderDeleteModal,
    onConfirmThenClose: onConfirmThenDelete,
  } = useConfirmationModal({
    needsConfirmation: true,
    onClose: handleDelete,
  });

  const documentTitle = attachment
    ? attachment.title || attachment.file_name || "Untitled document"
    : undefined;
  return (
    <div className="flex h-full flex-col">
      {guardModal}
      {renderDeleteModal({
        title: "Delete document?",
        message:
          "Are you sure you want to delete this document? This action cannot be undone.",
        confirmText: "Delete",
      })}
      <PageHeader
        className="pb-3"
        title={documentTitle}
        isLoading={!documentTitle}
        leadingAction={
          <IconButton
            accessibleLabel="Back to documents"
            variant={ButtonVariant.Tertiary}
            onClick={onClose}
            size={Size.SMALL}
            icon={<IconChevronLeft aria-hidden className="size-4" />}
          />
        }
        actions={
          attachment?.sync ? (
            <KnowledgeBaseSyncPill
              provider={attachment.sync.provider}
              lastSyncedAt={attachment.sync.synced_at}
            />
          ) : !isReadOnly ? (
            <Button
              variant={ButtonVariant.Warning}
              onClick={onConfirmThenDelete}
              isDisabled={isDeleting}
              size={Size.SMALL}
            >
              Delete
            </Button>
          ) : undefined
        }
      />
      {shownProposal ? (
        <CopilotProposalBanner
          // Lines up with the document body, which is inset by its padding.
          className="mx-6 mt-2"
          message="Showing changes suggested by Ask Alpharun."
          discardLabel="Discard changes"
          confirmLabel="Apply changes"
          onDiscard={() => {
            void resolveLinkedProposal(shownProposal, "discarded");
          }}
          onConfirm={() => {
            void applyLinkedProposal(shownProposal);
          }}
          isDisabled={isSaving || isResolvingProposal}
        />
      ) : null}
      <div className="flex min-h-0 flex-1">
        <AttachmentEditorContent
          // Wait for the draft as well as the document. An editor that
          // mounts with an empty draft records the body's arrival as an edit,
          // and undo would then empty the document.
          isLoading={isLoading || !isInitialized}
          isSaving={isSaving}
          isReadOnly={isReadOnly}
          editedBody={editedBody}
          savedBody={fetchedBody}
          setEditedBody={setEditedBody}
          suggestedBody={shownProposal?.data.body}
        />
      </div>
      {!isReadOnly && hasUnsavedChanges && !shownProposal ? (
        <ResourceSaveFooter
          onCancel={onDiscard}
          onSave={() => {
            void saveBody(editedBody);
          }}
          isSaving={isSaving}
        />
      ) : null}
    </div>
  );
};

function notifyProposalUnavailable() {
  showToast({
    variant: ToastVariant.Error,
    title: "This suggestion is no longer available.",
  });
}
